import React, { memo, type ReactElement, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  ApplicationIcon,
  Box,
  Button,
  Icon,
  ListRow,
  ListRowGroup,
  TabContent,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodMessage, SodReloader } from '../../../../../shared/components';
import { VIOLATIONS_DETAILS_TABS } from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { type RootState } from '../../../../../shared/types';
import { type ViolationMitigatingControls } from '../../../data-layer/detailsTypes';
import { detailsActions } from '../../../data-store/detailsSlice';
import { useHandleMitigatingControl } from '../../../hooks';

import ViolationsTabDetailsMitigatingLoader from './ViolationsTabDetailsMitigatingLoader';
import {
  MitigateCenterContent,
  MitigateTralingContent,
} from './ViolationsTabDetailsMitigatingShared';

import './ViolationsTabDetailsMitigating.css';

interface ViolationsTabDetailsMitigatingProps {
  sodKey: string;
  activeTab: string;
  className?: string;
}

/**
 * ViolationsTabDetailsMitigating
 *
 * TODO:
 * - Localize Strings
 * - Intigration testing
 */
function ViolationsTabDetailsMitigating({
  sodKey,
  activeTab,
  className,
}: ViolationsTabDetailsMitigatingProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const {
    sidepanel: { isSidePanelVisible },
    violationsTab: {
      mitigate: { selectedRowData },
    },
  } = useTypedSelector((state: RootState) => state.aag.details);

  const { loadMitigateRow, mitigatingControlRows, isError, isLoading } =
    useHandleMitigatingControl();

  const {
    aag: {
      global: { globalDateFormat },
    },
  } = useTypedSelector((state) => state);

  useEffect(() => {
    // Only call API when this tab is active
    if (activeTab === VIOLATIONS_DETAILS_TABS.TAB_MITIGATING && sodKey) {
      const requestData = {
        id: parseInt(sodKey, 10),
        violationId: sodKey,
      };

      loadMitigateRow(requestData).catch(() => {});
    }

    return undefined;
  }, [activeTab, loadMitigateRow, sodKey]);

  const handleRowClick = (row: ViolationMitigatingControls) => {
    dispatch(detailsActions.setSelectedMitigateRow(row));
    dispatch(detailsActions.setIsSidePanelVisible(true));
  };

  const mitigatingtabContentClasses = cx(
    'Sod-ViolationsTabDetailsMitigating',
    className
  );

  if (isLoading) {
    return <ViolationsTabDetailsMitigatingLoader />;
  }

  if (isError) {
    return (
      <SodReloader
        triggerRefresh={() => {
          if (sodKey) {
            const requestData = {
              id: parseInt(sodKey, 10),
              violationId: sodKey,
            };

            // Force a refresh by deleting the cache entry
            loadMitigateRow(requestData).catch(() => {});
          }
        }}
      />
    );
  }

  if (mitigatingControlRows.length === 0) {
    return (
      <SodMessage
        isSearchActive={false}
        isFilterActive={false}
        handleFilterClearBtn={() => {}}
      />
    );
  }

  const scrollWrapperClasses = cx(
    'Sod-ViolationsTabDetailsMitigating-nestedListGroupScrollArea',
    isSidePanelVisible &&
      'Sod-ViolationsTabDetailsMitigating-nestedListGroupScrollArea--isSidePanelVisible'
  );

  return (
    <TabContent
      value={activeTab}
      tabValue={VIOLATIONS_DETAILS_TABS.TAB_MITIGATING}
      className={mitigatingtabContentClasses}>
      <Box className='Sod-ViolationsTabDetailsMitigating-listHeading'>
        <Typography kind='h3'>
          {`${l('Applied Mitigating Control')} (${
            mitigatingControlRows.length
          })`}
        </Typography>
        {!isSidePanelVisible && (
          <Button
            isDisabled
            type='button'
            kind='outlined'
            onClick={() => {}}
            leftIcon={<Icon kind='Add' />}>
            {l('Add')}
          </Button>
        )}
      </Box>
      <Box tag='div' className={scrollWrapperClasses}>
        <ListRowGroup className='Sod-ViolationsTabDetailsMitigating-list'>
          {mitigatingControlRows.map((row) => (
            <ListRow
              key={row.id}
              centerContent={
                <MitigateCenterContent
                  row={row}
                  globalDateFormat={globalDateFormat}
                />
              }
              leadingComp={
                <ApplicationIcon icon='MitigatingControls' size='medium' />
              }
              trailingContent={
                !isSidePanelVisible ? <MitigateTralingContent /> : undefined
              }
              handleRowClick={() => handleRowClick(row)}
              isRowDetailsOpen={selectedRowData?.id === row.id}
              connectorDistance='1.625rem'
              isRowConnectTransitionEnabled
              isCheckboxDisabled
            />
          ))}
        </ListRowGroup>
      </Box>
    </TabContent>
  );
}

ViolationsTabDetailsMitigating.defaultProps = {
  className: null,
};

export default memo(ViolationsTabDetailsMitigating);
